/**
 * RadialGauge chart component for Dashboard V2.
 *
 * Implements a gauge chart using Chart.js Doughnut as base,
 * with a custom afterDraw plugin to display the center value.
 *
 * Data format expected from transformToChartJs:
 *   labels: ['', ''],
 *   datasets: [{ data: [value, max-value] }]
 * Options expected from buildChartOptions:
 *   cutout: '70%', circumference: 270, rotation: -135
 */

import { Chart as ChartJS, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Register required controllers
ChartJS.register(DoughnutController, ArcElement, Tooltip, Legend);

// Custom plugin to render the center value
const centerValuePlugin = {
  id: 'centerValue',
  afterDraw(chart: any) {
    const { ctx, chartArea, data } = chart;
    if (!data || !data.datasets || !data.datasets[0]) return;

    const value = data.datasets[0].data[0];
    if (value === undefined || value === null) return;

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.font = 'bold 32px sans-serif';
    ctx.fillStyle = '#333';
    ctx.fillText(String(value), centerX, centerY - 8);

    ctx.font = '14px sans-serif';
    ctx.fillStyle = '#999';
    ctx.fillText('%', centerX, centerY + 20);

    ctx.restore();
  },
};

interface RadialGaugeChartProps {
  data: any;
  options: any;
}

export function RadialGaugeChart({ data, options }: RadialGaugeChartProps) {
  return (
    <div data-testid="chart-radial-gauge" style={{ width: '100%', height: '100%' }}>
      <Doughnut
      data={{ ...data, labels: ['', ''] }}
      options={{
        ...options,
        cutout: '70%',
        circumference: 270,
        rotation: -135,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
      }}
      plugins={[centerValuePlugin]}
    />
    </div>
  );
}
